import {
  type PropertySet,
  TrimbimGeometryType,
} from '@technology/web3d-plugin-trimbim';
import { UnreachableCaseError } from 'ts-essentials';
import type {
  AlignmentProperties,
  AlignmentPropertiesModel,
  AlignmentType,
} from './alignment-properties-reader.types';

const ALIGNMENT_TYPES: readonly AlignmentType[] = [
  'left-rail',
  'right-rail',
  'center-line',
];

const ALIGNMENT_PROPERTIES_GROUP = 'AlignmentProperties';
const UNSCANNED_TAG = 'unscanned';
const PROPERTY_NAMES = [
  'beginStation',
  'endStation',
  'name',
  'tag',
  'type',
] as const;
type PropertyName = (typeof PROPERTY_NAMES)[number];
const SELECTABLE_GEOMETRY_TYPES = [
  TrimbimGeometryType.AlignmentCurve,
  TrimbimGeometryType.GraphicsContainer,
];

export function isRail(properties: AlignmentProperties | undefined): boolean {
  return properties?.type === 'left-rail' || properties?.type === 'right-rail';
}

/**
 * Reads the `AlignmentProperties` property sets of trimbim models and derives
 * navigation data from them. Results are cached per model id for the lifetime
 * of one viewer; call `invalidate` when a model is removed.
 */
export class AlignmentPropertiesReader {
  readonly #properties = new Map<string, Promise<AlignmentProperties[]>>();
  readonly #railRouting = new Map<string, Promise<Map<number, number>>>();
  readonly #selectableEntityIds = new Map<string, Promise<number[]>>();

  public getProperties(
    model: AlignmentPropertiesModel
  ): Promise<AlignmentProperties[]> {
    return memoize(this.#properties, model.modelId, () =>
      readAlignmentProperties(model)
    );
  }

  public async isAlignmentModel(
    model: AlignmentPropertiesModel
  ): Promise<boolean> {
    return (await this.getProperties(model)).length > 0;
  }

  public async findProperties(
    model: AlignmentPropertiesModel,
    entityId: number
  ): Promise<AlignmentProperties | undefined> {
    return (await this.getProperties(model)).find(
      (properties) => properties.entityId === entityId
    );
  }

  /** Rails are navigated along the center line spanning the same stations. */
  public async resolveNavigableEntityId(
    model: AlignmentPropertiesModel,
    entityId: number
  ): Promise<number> {
    const routing = await memoize(this.#railRouting, model.modelId, async () =>
      buildRailRouting(await this.getProperties(model))
    );
    return routing.get(entityId) ?? entityId;
  }

  public getSelectableEntityIds(
    model: AlignmentPropertiesModel
  ): Promise<number[]> {
    return memoize(this.#selectableEntityIds, model.modelId, async () =>
      (
        await Promise.all(
          SELECTABLE_GEOMETRY_TYPES.map((type) =>
            model.findEntitiesByGeometryType(type)
          )
        )
      ).flat()
    );
  }

  public async getNavigableEntityIds(
    model: AlignmentPropertiesModel
  ): Promise<number[]> {
    const properties = await this.getProperties(model);
    if (properties.length === 0) {
      return [];
    }
    const entityIds = await model.findEntitiesByGeometryType(
      TrimbimGeometryType.GraphicsContainer
    );
    return entityIds.filter(
      (entityId) =>
        !isRail(properties.find((entry) => entry.entityId === entityId))
    );
  }

  /** The section tag names scanned alignments; unscanned ones fall back to their name. */
  public async getDisplayName(
    model: AlignmentPropertiesModel,
    entityId: number
  ): Promise<string> {
    const properties = await this.findProperties(model, entityId);
    return properties?.tag && properties.tag !== UNSCANNED_TAG
      ? properties.tag
      : properties?.name || '';
  }

  public invalidate(modelId: string): void {
    this.#properties.delete(modelId);
    this.#railRouting.delete(modelId);
    this.#selectableEntityIds.delete(modelId);
  }

  /** Drops entity-id lookups of every model while keeping the properties read so far. */
  public clearEntityLookups(): void {
    this.#railRouting.clear();
    this.#selectableEntityIds.clear();
  }
}

function memoize<T>(
  cache: Map<string, Promise<T>>,
  key: string,
  compute: () => Promise<T>
): Promise<T> {
  const cached = cache.get(key);
  if (cached) {
    return cached;
  }
  const pending = compute();
  cache.set(key, pending);
  // A failed lookup must not stick: the next caller retries.
  pending.catch(() => {
    if (cache.get(key) === pending) {
      cache.delete(key);
    }
  });
  return pending;
}

async function readAlignmentProperties(
  model: AlignmentPropertiesModel
): Promise<AlignmentProperties[]> {
  const entities = await model.getEntities();
  const propertySets = await Promise.all(
    entities.map(async (entity) => ({
      entityId: entity.id,
      group: (await model.getProperties(entity.id)).find(
        (propertySet) => propertySet.name === ALIGNMENT_PROPERTIES_GROUP
      ),
    }))
  );
  return propertySets.flatMap(({ entityId, group }) =>
    group ? [toAlignmentProperties(entityId, group)] : []
  );
}

function toAlignmentProperties(
  entityId: number,
  group: PropertySet
): AlignmentProperties {
  const result: {
    -readonly [K in keyof AlignmentProperties]: AlignmentProperties[K];
  } = { entityId };
  for (const property of group.properties ?? []) {
    const name = toPropertyName(property.name);
    if (!name) {
      continue;
    }
    const value = String(property.value);
    switch (name) {
      case 'beginStation':
        result.beginStation = parseFloat(value);
        break;
      case 'endStation':
        result.endStation = parseFloat(value);
        break;
      case 'name':
        result.name = value;
        break;
      case 'tag':
        result.tag = value;
        break;
      case 'type':
        result.type = toAlignmentType(value);
        break;
      default:
        throw new UnreachableCaseError(name);
    }
  }
  return result;
}

function toPropertyName(name: string): PropertyName | undefined {
  const camelCaseName = toCamelCase(name);
  return PROPERTY_NAMES.find((propertyName) => propertyName === camelCaseName);
}

function toAlignmentType(value: string): AlignmentType | undefined {
  const lowerCaseValue = value.toLowerCase();
  return ALIGNMENT_TYPES.find((type) => type === lowerCaseValue);
}

function buildRailRouting(
  properties: readonly AlignmentProperties[]
): Map<number, number> {
  const routing = new Map<number, number>();
  for (const entry of properties) {
    const centerLine = isRail(entry)
      ? properties.find(
          (other) =>
            other.type === 'center-line' &&
            other.beginStation === entry.beginStation &&
            other.endStation === entry.endStation
        )
      : undefined;
    routing.set(entry.entityId, centerLine?.entityId ?? entry.entityId);
  }
  return routing;
}

function toCamelCase(name: string): string {
  return `${name[0].toLowerCase()}${name.slice(1)}`;
}
